<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { storeToRefs } from "pinia"
import { useHomeWorkspaceStore } from "@modules/home/stores/homeStore.store"
import type { BuildingVisibleToEntry } from "@types"
import type { ItemFolderData } from "@core/components/item/ItemFolder.vue"

defineOptions({
    name: "HomeFolderDialogs",
})

// ================================
// PROPS & EMITS
// ================================
// Pas de props : orchestration des dialogues dossier pilotée par le store `homeWorkspace`.

// ================================
// COMPOSABLES
// ================================
const homeWorkspace = useHomeWorkspaceStore()
const { modal } = storeToRefs(homeWorkspace)

// ================================
// COMPUTED
// ================================
/**
 * Nom du dossier affiché dans la modal de suppression (aligné métadonnée `name`).
 */
const getFolderRemoveDisplayName = computed(() => {
    if (modal.value.kind !== "folderRemove") return ""
    return String(modal.value.meta.name ?? "")
})

/**
 * Parent du dossier déplacé (contexte liste), pour détecter un déplacement inutile.
 */
const getFolderMoveParentId = computed((): string | null => {
    if (modal.value.kind !== "folderMove") return null
    const raw = modal.value.meta.currentFolderId as
        | string
        | string[]
        | undefined
        | null
    if (raw === undefined || raw === null || raw === "") return null
    return Array.isArray(raw) ? (raw[0] ?? null) : String(raw)
})

/** Id dossier pour le dialogue de partage (liste d’accès). */
const getFolderAccessFolderId = computed(
    () => (modal.value.meta.folderId as string) ?? ""
)

// ================================
// STATE
// ================================
/**
 * Copie mutable du récap dossier pour rafraîchir les avatars après sauvegarde des accès.
 */
const folderAccessItemPatched = ref<ItemFolderData | null>(null)
/** Nom saisi pour le renommage d’un dossier. */
const folderName = ref("")
/** Nom saisi pour la création d’un dossier. */
const createFolderName = ref("")
/** Verrou UI pendant les appels API. */
const loading = ref(false)

// ================================
// INTERACTIONS
// ================================
/**
 * Ferme le dialogue courant.
 */
function onClose(): void {
    folderAccessItemPatched.value = null
    homeWorkspace.closeModal()
}

/**
 * Après mise à jour des accès dossier : store (liste dossiers) et en-tête du dialogue.
 */
function onFolderAccessUpdated(payload: {
    folderId: string
    visibleTo: BuildingVisibleToEntry[]
}): void {
    homeWorkspace.applyFolderAccessUpdate(payload.folderId, payload.visibleTo)
    const row = folderAccessItemPatched.value
    if (row && row._id === payload.folderId) {
        folderAccessItemPatched.value = {
            ...row,
            visible_to: payload.visibleTo,
        }
    }
}

/**
 * Crée un dossier dans le dossier courant (ou à la racine).
 */
async function onConfirmCreate(): Promise<void> {
    if (!createFolderName.value.trim()) return
    loading.value = true
    try {
        await homeWorkspace.submitCreateFolder(
            createFolderName.value.trim(),
            homeWorkspace.currentFolderId
        )
    } finally {
        loading.value = false
    }
}

/**
 * Enregistre le nouveau nom du dossier.
 */
async function onConfirmRename(): Promise<void> {
    const folderId = modal.value.meta.folderId as string
    if (!folderId || !folderName.value.trim()) return
    loading.value = true
    try {
        await homeWorkspace.submitRenameFolder(
            folderId,
            folderName.value.trim()
        )
    } finally {
        loading.value = false
    }
}

/**
 * Supprime le dossier après confirmation.
 */
async function onConfirmRemove(): Promise<void> {
    const folderId = modal.value.meta.folderId as string
    if (!folderId) return
    loading.value = true
    try {
        await homeWorkspace.submitDeleteFolder(folderId)
    } finally {
        loading.value = false
    }
}

// ================================
// WATCHERS
// ================================
watch(
    () => modal.value,
    (m) => {
        if (m.kind === "folderRename") {
            folderName.value = (m.meta.name as string) ?? ""
        }
        if (m.kind === "folderCreate") {
            createFolderName.value = ""
        }
        if (m.kind === "folderAccess") {
            const id = (m.meta.folderId as string) ?? ""
            const raw = m.meta.itemFolder as ItemFolderData | undefined
            folderAccessItemPatched.value = raw
                ? { ...raw }
                : { _id: id, name: id || "-" }
        } else {
            folderAccessItemPatched.value = null
        }
    },
    { deep: true, immediate: true }
)
</script>

<template>
    <!-- ================================ -->
    <!-- DIALOGUE — CRÉER DOSSIER -->
    <!-- ================================ -->
    <BaseDialog
        :visible="modal.kind === 'folderCreate'"
        modal
        header="Créer un dossier"
        class="w-[min(100%,24rem)]"
        @update:visible="(v: boolean) => !v && onClose()"
    >
        <div class="flex flex-col gap-3 py-2">
            <label class="text-sm text-surface-600" for="folder-create-input"
                >Nom du dossier</label
            >
            <BaseInputText
                id="folder-create-input"
                v-model="createFolderName"
                class="w-full"
                autofocus
                @keyup.enter="onConfirmCreate"
            />
        </div>
        <template #footer>
            <BaseButton label="Annuler" severity="secondary" @click="onClose" />
            <BaseButton
                label="Créer"
                :loading="loading"
                :disabled="!createFolderName.trim()"
                @click="onConfirmCreate"
            />
        </template>
    </BaseDialog>

    <!-- ================================ -->
    <!-- DIALOGUE — RENOMMER DOSSIER -->
    <!-- ================================ -->
    <BaseDialog
        :visible="modal.kind === 'folderRename'"
        modal
        header="Renommer le dossier"
        class="w-[min(100%,24rem)]"
        @update:visible="(v: boolean) => !v && onClose()"
    >
        <div class="flex flex-col gap-3 py-2">
            <label class="text-sm text-surface-600" for="folder-rename-input"
                >Nom</label
            >
            <BaseInputText
                id="folder-rename-input"
                v-model="folderName"
                class="w-full"
                @keyup.enter="onConfirmRename"
            />
        </div>
        <template #footer>
            <BaseButton label="Annuler" severity="secondary" @click="onClose" />
            <BaseButton
                label="Enregistrer"
                :loading="loading"
                @click="onConfirmRename"
            />
        </template>
    </BaseDialog>

    <!-- ================================ -->
    <!-- DIALOGUE — SUPPRIMER DOSSIER -->
    <!-- ================================ -->
    <BaseDialog
        :visible="modal.kind === 'folderRemove'"
        modal
        header="Supprimer le dossier ?"
        class="w-[min(100%,28rem)]"
        @update:visible="(v: boolean) => !v && onClose()"
    >
        <div class="flex flex-col gap-3 py-2">
            <p class="m-0 text-sm leading-relaxed text-surface-700">
                Êtes-vous sûr de vouloir supprimer le dossier
                <span
                    class="inline-block max-w-full min-w-0 font-semibold text-surface-900 align-bottom"
                    :title="getFolderRemoveDisplayName"
                    >{{ getFolderRemoveDisplayName }}</span
                >
                ?
            </p>
            <BaseMessage severity="warn" :closable="false">
                Attention, tous les bâtiments et dossiers contenus dans le
                dossier
                <span class="font-semibold">{{
                    getFolderRemoveDisplayName
                }}</span>
                se retrouveront dans le dossier "Mon espace".
            </BaseMessage>
        </div>
        <template #footer>
            <BaseButton label="Annuler" severity="secondary" @click="onClose" />
            <BaseButton
                label="Supprimer"
                severity="danger"
                :loading="loading"
                @click="onConfirmRemove"
            />
        </template>
    </BaseDialog>

    <!-- ================================ -->
    <!-- DIALOGUE — DÉPLACER DOSSIER -->
    <!-- ================================ -->
    <HomeMoveToFolderDialog
        :visible="modal.kind === 'folderMove'"
        mode="folder"
        :from-folder-id="(modal.meta.fromFolderId as string) ?? ''"
        :source-parent-folder-id="getFolderMoveParentId"
        @update:visible="(v: boolean) => !v && onClose()"
    />

    <!-- ================================ -->
    <!-- DIALOGUE — PARTAGE DOSSIER -->
    <!-- ================================ -->
    <BaseDialog
        :visible="modal.kind === 'folderAccess'"
        modal
        header="Partage d'accès"
        class="w-[min(100%,50rem)] max-h-[90vh]"
        @update:visible="(v: boolean) => !v && onClose()"
    >
        <div class="flex flex-col gap-4 overflow-y-auto py-1">
            <ItemFolder
                v-if="folderAccessItemPatched"
                :folder="folderAccessItemPatched"
            />
            <p v-else class="text-sm text-surface-600">
                Dossier :
                <span class="font-medium">{{ getFolderAccessFolderId }}</span>
            </p>
            <p class="text-sm text-surface-600">
                Cochez les collaborateurs autorisés à accéder à ce dossier.
            </p>
            <AccessFolderListUser
                v-if="getFolderAccessFolderId"
                :folder-id="getFolderAccessFolderId"
                class="max-w-none"
                list-height="min(380px, 45vh)"
                @access-updated="onFolderAccessUpdated"
            />
        </div>
        <template #footer>
            <BaseButton label="Fermer" severity="secondary" @click="onClose" />
        </template>
    </BaseDialog>
</template>
